/**
 * ABOUTPAGE.JS | Micheals Infrastructure Advisory
 * COMPONENT: Mission & Philosophy Interface
 * VERSION: 2026.CORE.V5.UNIFIED
 */

import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Coffee, Heart, Globe, ChevronRight, Microscope, Target, Cpu } from 'lucide-react';
import SEO from '../components/SEO';

const AboutPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
  };

  return (
    <div className="bg-[#f8f8f7] text-stone-950 min-h-screen selection:bg-stone-200">
      <SEO 
        title="Our Governance | Micheals Infrastructure Advisory" 
        description="Fiduciary technical oversight and logic-first architectural design." 
      />

      {/* --- HERO: THE GOVERNANCE STANCE --- */}
      <header className="relative pt-60 pb-32 px-6 bg-stone-950 overflow-hidden">
        <div className="absolute inset-0 blueprint-grid opacity-10 pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10 px-6 md:px-24">
          <motion.div {...fadeInUp}>
            <span className="text-[10px] font-black tracking-[0.5em] uppercase text-amber-600 mb-6 block">
              Governance // Technical Architecture
            </span>
            <h1 className="text-6xl md:text-8xl font-serif italic text-white tracking-tighter leading-[0.9] mb-10 max-w-4xl">
              Eliminating logic gaps <br/>
              <span className="not-italic font-light text-stone-500 text-5xl md:text-7xl">before the ground breaks.</span>
            </h1>
          </motion.div>
        </div>
      </header>

      {/* --- PHASE 02: THE PRINCIPAL'S MANDATE --- */}
      <section className="py-32 px-6 md:px-24 max-w-7xl mx-auto border-b border-stone-200">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">
          <motion.div {...fadeInUp}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-stone-200 rounded-sm text-stone-950 text-[10px] font-black uppercase tracking-widest mb-8">
              <Heart size={12} /> Fiduciary Integrity & Logic
            </div>
            <h2 className="text-4xl md:text-5xl font-serif italic text-stone-950 mb-8 tracking-tight">
              A Principal Advocate, <br/>
              <span className="not-italic font-light text-stone-400">Not Just a Consultant.</span>
            </h2>
            <div className="space-y-6 text-stone-600 font-light text-lg leading-relaxed italic border-l-2 border-stone-300 pl-8">
              <p>"At Micheals Infrastructure Advisory, we bridge the gap between architectural vision and operational reality. We safeguard asset investments by eliminating the disconnect between design, planning, and execution."</p>
              <p>"By resolving schematic deficiencies at the expert level, we provide the forensic auditing necessary to ensure every build is resilient, compliant, and optimized for long-term ROI."</p>
              <p className="not-italic font-black text-stone-950 tracking-tight pt-4">— Operator | Principal</p>
            </div>
          </motion.div>

          <motion.div {...fadeInUp} className="bg-white p-12 rounded-sm border border-stone-200 shadow-sm">
            <h3 className="text-[10px] uppercase tracking-[0.4em] font-black text-stone-400 mb-10">Governance Standards</h3>
            <div className="grid gap-8">
              {[
                { icon: Coffee, title: "Clear Communication", desc: "Expert guidance, stripped of industry gatekeeping and unnecessary jargon." },
                { icon: ShieldCheck, title: "Fiduciary Transparency", desc: "Third-party auditing where your assets remain the absolute priority." },
                { icon: Globe, title: "National Standards", desc: "Strategic technical oversight for complex infrastructure projects." }
              ].map((item, i) => (
                <div key={i} className="flex gap-6">
                  <div className="w-10 h-10 bg-stone-50 border border-stone-100 rounded-sm flex items-center justify-center shrink-0">
                    <item.icon className="text-amber-600" size={18} />
                  </div>
                  <div>
                    <h4 className="text-stone-950 font-black text-xs uppercase tracking-wider mb-2">{item.title}</h4>
                    <p className="text-stone-500 font-light text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* --- PHASE 03: THE GOVERNANCE PILLARS --- */}
      <section className="py-48 px-6 md:px-24 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeInUp} className="text-center mb-24">
            <h2 className="text-4xl md:text-6xl font-serif italic mb-6">Our Three Pillars</h2>
            <div className="h-px w-24 bg-amber-600 mx-auto mb-8" />
            <p className="text-stone-500 font-light text-xl max-w-2xl mx-auto">Operating as the "Technical Source of Truth."</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Microscope, title: "Forensic Architecture", bullets: ["Asset Integrity Audits", "Legacy Systems Recovery", "Deficiency Mapping"] },
              { icon: Target, title: "Risk Mitigation", bullets: ["Blueprint Validation", "Vendor Logic Audits", "Compliance Oversight"] },
              { icon: Cpu, title: "Autonomous Governance", bullets: ["Standardized Compliance", "Automated Verification", "Protocol Documentation"] }
            ].map((pillar, i) => (
              <motion.div key={i} {...fadeInUp} className="bg-[#f8f8f7] p-12 border border-stone-200 hover:border-amber-600 transition-all">
                <div className="w-16 h-16 bg-stone-950 rounded-sm flex items-center justify-center mb-10"><pillar.icon className="text-amber-600" size={28} /></div>
                <h4 className="text-2xl font-serif italic mb-10 tracking-tight">{pillar.title}</h4>
                <ul className="space-y-4">
                  {pillar.bullets.map((b, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-[10px] uppercase tracking-widest font-black text-stone-500">
                      <div className="w-1 h-1 bg-amber-600 rounded-full" /> {b}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- FINAL CTA: THE PLEDGE --- */}
      <section className="py-32 px-6 text-center">
        <button onClick={() => window.location.href = '/'} className="inline-flex items-center gap-6 bg-stone-950 text-white px-12 py-7 uppercase tracking-[0.4em] font-black text-[10px] hover:bg-amber-700 transition-all shadow-2xl">
          Return to Gateway <ChevronRight size={18} />
        </button>
      </section>
    </div>
  );
};

export default AboutPage;